import clsx from 'clsx'
import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from 'react'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
  description?: ReactNode
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, id, ...rest },
  ref,
) {
  // useId is SSR-safe (deterministic across server + client renders) and
  // hydration-stable. The previous Math.random() fallback re-rolled on every
  // render which broke <label htmlFor> linkage as soon as React re-rendered
  // the parent, and produced different IDs on server vs. client.
  const generated = useId()
  const inputId = id ?? generated
  const descId = description ? `${inputId}-desc` : undefined

  const inputEl = (
    <input
      ref={ref}
      id={inputId}
      type="checkbox"
      aria-describedby={descId}
      className={clsx(
        'h-4 w-4 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-2)]',
        'accent-[var(--accent)] cursor-pointer',
        'focus-visible:shadow-[var(--focus-ring)] outline-none',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
      {...rest}
    />
  )

  if (!label && !description) return inputEl

  return (
    <label
      htmlFor={inputId}
      className="inline-flex items-start gap-2 cursor-pointer text-[14px] text-[var(--text)]"
    >
      {inputEl}
      <span className="flex flex-col">
        {label ? <span>{label}</span> : null}
        {description ? (
          <span id={descId} className="text-[13px] text-[var(--text-2)]">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  )
})
